import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import axios from 'axios';
import { API_BASE, WS_BASE, STEP_NAMES, STEP_ETA } from '../config';

const S_COLOR = {
  pending:    'var(--muted)',
  running:    'var(--rust)',
  done:       'var(--green)',
  complete:   'var(--green)',
  completed:  'var(--green)',
  failed:     'var(--red)',
};

function StepRow({ num, status, name, eta, duration, onDownload }) {
  const isRunning  = status === 'running';
  const isDone     = status === 'done' || status === 'completed' || status === 'complete';
  const isFailed   = status === 'failed';
  const color      = S_COLOR[status] || 'var(--muted)';

  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 14,
      padding: '13px 20px',
      borderBottom: '1px solid var(--border)',
      background: isRunning ? 'var(--rust-soft)' : 'transparent',
      transition: 'background 0.2s',
    }}>
      <div style={{
        width: 26, height: 26, borderRadius: 4, flexShrink: 0,
        border: `1px solid ${color}`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontFamily: 'IBM Plex Mono, monospace', fontSize: 11,
        color,
      }}>
        {isDone ? '✓' : isFailed ? '✕' : isRunning ? '◌' : num}
      </div>

      <div style={{ flex: 1 }}>
        <div style={{
          fontSize: 13, fontWeight: 400,
          color: status === 'pending' ? 'var(--muted)' : 'var(--white)',
          marginBottom: 2,
        }}>{name}</div>
        <div style={{
          fontSize: 10, color: 'var(--muted)',
          fontFamily: 'IBM Plex Mono, monospace',
        }}>
          {isRunning ? `running · est. ${eta}` :
           isDone    ? `${duration ? duration + 's' : 'done'}` :
           isFailed  ? 'failed' :
           `est. ${eta}`}
        </div>
      </div>

      {isRunning && (
        <div style={{
          width: 60, height: 2,
          background: 'var(--faint)', borderRadius: 2, overflow: 'hidden',
        }}>
          <div style={{
            height: '100%', width: '35%',
            background: 'var(--rust)',
            animation: 'slide 1.4s ease-in-out infinite',
          }} />
        </div>
      )}

      {isDone && onDownload && (
        <button onClick={() => onDownload(num)} className="btn btn-ghost btn-sm">↓</button>
      )}
    </div>
  );
}

export default function JobProgress() {
  const { jobId }           = useParams();
  const { user }            = useAuth();
  const [job, setJob]       = useState(null);
  const [logs, setLogs]     = useState([]);
  const [q, setQ]           = useState('');
  const [answer, setAnswer] = useState('');
  const [asking, setAsking] = useState(false);
  const [viewerUrl, setViewerUrl] = useState('');
  const logsRef = useRef();

  const headers = async () => ({
    'Authorization': `Bearer ${user ? await user.getIdToken() : 'stub'}`,
    'x-api-key': 'test-key-123',
  });

  const fetchJob = async () => {
    try {
      const { data } = await axios.get(
        `${API_BASE}/jobs/${jobId}`,
        { headers: await headers() }
      );
      setJob(data);

      // Fetch viewer URL when complete
      if ((data.status === 'complete' || data.status === 'completed') && !viewerUrl) {
        try {
          const { data: vd } = await axios.get(
            `${API_BASE}/jobs/${jobId}/viewer`,
            { headers: await headers() }
          );
          // Append job + token params for auto-load mode
          const token = user ? await user.getIdToken() : 'stub';
          const baseUrl = vd.viewer_url.split('?')[0];
          const autoUrl = `${baseUrl}?job=${jobId}&api=${API_BASE}&token=${encodeURIComponent(token)}`;
          setViewerUrl(autoUrl);
        } catch(e) { /* viewer not ready yet */ }
      }
    } catch(e) { console.error(e); }
  };

  useEffect(() => {
    fetchJob();
    const iv = setInterval(fetchJob, 5000);
    return () => clearInterval(iv);
  }, [jobId]);

  useEffect(() => {
    const ws = new WebSocket(`${WS_BASE}/jobs/${jobId}/stream`);
    ws.onmessage = (e) => {
      const msg = JSON.parse(e.data);
      if (msg.type === 'log') {
        setLogs(p => [...p.slice(-199), msg.line]);
        setTimeout(() => {
          if (logsRef.current)
            logsRef.current.scrollTop = logsRef.current.scrollHeight;
        }, 40);
      }
      if (msg.type === 'progress') {
        setJob(p => p ? {
          ...p, current_step: msg.step,
          steps: p.steps?.map(s =>
            s.step === msg.step ? { ...s, status: msg.status } : s
          )
        } : p);
      }
    };
    return () => ws.close();
  }, [jobId]);

  const handleDownload = async (stepNum) => {
    try {
      const { data } = await axios.get(
        `${API_BASE}/jobs/${jobId}/steps/${stepNum}/download`,
        { headers: await headers() }
      );
      window.open(data.download_url, '_blank');
    } catch(e) { alert('Download not available: ' + e.message); }
  };

  const handleQuery = async () => {
    if (!q.trim()) return;
    setAsking(true);
    try {
      const { data } = await axios.post(
        `${API_BASE}/jobs/${jobId}/query?question=${encodeURIComponent(q)}`,
        null, { headers: await headers() }
      );
      setAnswer(data.answer);
    } catch(e) { setAnswer('Error: ' + e.message); }
    setAsking(false);
  };

  const isComplete = job?.status === 'complete' || job?.status === 'completed';
  const done = job?.steps?.filter(s =>
    s.status === 'done' || s.status === 'completed' || s.status === 'complete'
  ).length || 0;
  const totalSteps = 10; // steps 1-10; step 0 shown separately
  const pct = Math.round((done / totalSteps) * 100);
  const SC = {
    queued: 'var(--muted)', processing: 'var(--rust)',
    complete: 'var(--green)', completed: 'var(--green)', failed: 'var(--red)'
  };

  return (
    <div style={{ maxWidth: 800, margin: '0 auto', padding: '40px 32px' }}>
      <Link to="/" style={{
        color: 'var(--muted)', textDecoration: 'none',
        fontSize: 12, fontFamily: 'IBM Plex Mono, monospace',
        display: 'inline-flex', alignItems: 'center', gap: 6, marginBottom: 28,
      }}>← Back</Link>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16, marginBottom: 28 }}>
        <div style={{ flex: 1 }}>
          <div style={{
            fontFamily: 'IBM Plex Mono, monospace', fontSize: 10,
            color: 'var(--muted)', letterSpacing: 2, marginBottom: 8,
            textTransform: 'uppercase',
          }}>Job · {jobId?.slice(0, 8)}</div>
          <h1 style={{ fontSize: 24, fontWeight: 500 }}>3D Reconstruction</h1>
        </div>
        {job?.status && (
          <div style={{
            fontSize: 11, fontFamily: 'IBM Plex Mono, monospace',
            color: SC[job.status] || 'var(--muted)',
            padding: '4px 12px', borderRadius: 20,
            border: `1px solid ${SC[job.status] || 'var(--border)'}`,
            opacity: 0.8,
          }}>{job.status}</div>
        )}
      </div>

      {/* Progress bar */}
      <div style={{
        background: 'var(--surface)', border: '1px solid var(--border)',
        borderRadius: 8, padding: '16px 20px', marginBottom: 16,
      }}>
        <div style={{
          display: 'flex', justifyContent: 'space-between',
          marginBottom: 10, fontFamily: 'IBM Plex Mono, monospace', fontSize: 11,
        }}>
          <span style={{ color: 'var(--muted)' }}>{done} of {totalSteps} steps</span>
          <span style={{ color: pct === 100 ? 'var(--green)' : 'var(--rust)' }}>{pct}%</span>
        </div>
        <div style={{ height: 3, background: 'var(--faint)', borderRadius: 2 }}>
          <div style={{
            height: '100%', width: `${pct}%`,
            background: pct === 100
              ? 'var(--green)'
              : 'linear-gradient(90deg, var(--rust-dim), var(--rust))',
            borderRadius: 2, transition: 'width 0.5s ease',
          }} />
        </div>
      </div>

      {/* Viewer URL — show when complete */}
      {isComplete && (
        <div style={{
          background: 'rgba(200,240,74,0.06)',
          border: '1px solid rgba(200,240,74,0.3)',
          borderRadius: 8, padding: '14px 20px',
          marginBottom: 16,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <div>
            <div style={{
              fontFamily: 'IBM Plex Mono, monospace', fontSize: 9,
              color: '#C8F04A', letterSpacing: 2,
              textTransform: 'uppercase', marginBottom: 4,
            }}>3D Viewer Ready</div>
            <div style={{ fontSize: 12, color: 'var(--muted)' }}>
              Interactive 3D model with measurements and AI assistant
            </div>
          </div>
          <a href={`/viewer/${jobId}`}
            className="btn btn-primary"
            style={{ textDecoration: 'none', whiteSpace: 'nowrap', marginLeft: 16 }}>
            Open Viewer →
          </a>
        </div>
      )}

      {/* Steps */}
      <div style={{
        background: 'var(--surface)', border: '1px solid var(--border)',
        borderRadius: 8, overflow: 'hidden', marginBottom: 16,
      }}>
        {/* Step 0 — Video Intelligence description row */}
        {(() => {
          const s0 = job?.steps?.find(s => s.step === 0 || s.step_number === 0);
          const s0status = s0?.status || (job?.current_step >= 1 ? 'done' : job?.status === 'processing' ? 'running' : 'pending');
          return (
            <div>
              <StepRow key={0} num={0}
                status={s0status}
                name="Video Intelligence"
                eta="~15s"
                duration={s0?.duration_s}
                onDownload={null}
              />
              {(s0status === 'running' || s0status === 'done' || s0status === 'completed') && (
                <div style={{
                  padding: '10px 20px 14px 60px',
                  borderBottom: '1px solid var(--border)',
                  background: 'var(--elevated)',
                }}>
                  <div style={{
                    fontSize: 12, color: 'var(--muted)', lineHeight: 1.7,
                    fontStyle: 'italic',
                  }}>
                    Analysing video frames to identify surfaces, geometry, scale, and lighting conditions —
                    establishing scene context to guide reconstruction accuracy and label detection across all steps.
                  </div>
                </div>
              )}
            </div>
          );
        })()}
        {Array.from({ length: totalSteps + 1 }, (_, i) => i).map(num => {
          const step = job?.steps?.find(s => s.step === num || s.step_number === num);
          return (
            <StepRow key={num} num={num}
              status={step?.status || 'pending'}
              name={STEP_NAMES[num]} eta={STEP_ETA[num]}
              duration={step?.duration_s}
              onDownload={num > 0 && [1,3,4,5,6,7,8,9,10].includes(num) ? handleDownload : null}
            />
          );
        })}
      </div>

      {/* Logs */}
      <div style={{
        background: '#080808', border: '1px solid var(--border)',
        borderRadius: 8, overflow: 'hidden', marginBottom: 16,
      }}>
        <div style={{
          padding: '8px 16px', borderBottom: '1px solid var(--border)',
          fontFamily: 'IBM Plex Mono, monospace', fontSize: 9,
          color: 'var(--muted)', letterSpacing: 2, textTransform: 'uppercase',
          display: 'flex', justifyContent: 'space-between',
        }}>
          <span>Logs</span>
          <span>{logs.length} lines</span>
        </div>
        <div ref={logsRef} style={{
          height: 150, overflowY: 'auto', padding: '10px 16px',
          fontFamily: 'IBM Plex Mono, monospace', fontSize: 11,
          color: 'var(--muted)', lineHeight: 1.8,
        }}>
          {logs.length === 0
            ? <span style={{ color: 'var(--faint)' }}>Waiting for pipeline...</span>
            : logs.map((l, i) => (
                <div key={i} style={{
                  color: l.includes('ERROR') ? 'var(--red)' : 'var(--muted)',
                }}>{l}</div>
              ))
          }
        </div>
      </div>

      {/* Claude Agent */}
      {isComplete && (
        <div style={{
          background: 'var(--surface)', border: '1px solid var(--border)',
          borderRadius: 8, overflow: 'hidden',
        }}>
          <div style={{
            padding: '12px 20px', borderBottom: '1px solid var(--border)',
            fontFamily: 'IBM Plex Mono, monospace', fontSize: 9,
            color: 'var(--rust)', letterSpacing: 2, textTransform: 'uppercase',
          }}>✦ Spatial Agent</div>
          <div style={{ padding: '18px 20px' }}>
            {answer && (
              <div style={{
                background: 'var(--elevated)', border: '1px solid var(--border)',
                borderRadius: 6, padding: '12px 14px',
                marginBottom: 14, fontSize: 14,
                color: 'var(--white)', lineHeight: 1.7,
              }}>{answer}</div>
            )}
            <div style={{ display: 'flex', gap: 8 }}>
              <input
                value={q} onChange={e => setQ(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleQuery()}
                placeholder="What is the floor area?"
                style={{
                  flex: 1, background: 'var(--elevated)',
                  border: '1px solid var(--border)',
                  borderRadius: 5, padding: '9px 13px',
                  color: 'var(--white)', fontSize: 13,
                  fontFamily: 'DM Sans, sans-serif', outline: 'none',
                }}
              />
              <button onClick={handleQuery}
                disabled={asking || !q.trim()}
                className="btn btn-primary"
                style={{ opacity: asking ? 0.5 : 1 }}>
                {asking ? '...' : 'Ask'}
              </button>
            </div>
            <div style={{ marginTop: 10, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {[
                'What is the floor area?',
                'How much paint do I need for the walls?',
                'Can a 2.2m sofa fit along the shorter wall?',
                'What AC tonnage is recommended?',
              ].map(sq => (
                <button key={sq} onClick={() => setQ(sq)} style={{
                  background: 'transparent', border: '1px solid var(--border)',
                  borderRadius: 4, padding: '4px 10px',
                  color: 'var(--muted)', fontSize: 11,
                  fontFamily: 'IBM Plex Mono, monospace', cursor: 'pointer',
                }}>{sq}</button>
              ))}
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes slide {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(350%); }
        }
      `}</style>
    </div>
  );
}
